// src/features/task/create/useTaskCreateForm.ts
// Everything a task-creation surface has to know, in one hook.
//
// There are two surfaces — the full page (/tasks/new) and the quick drawer over
// the task list — and they must not drift apart. What a task *is* does not
// change with the size of the window it is typed into: the same
// `ResolveTaskContext` resolution, the same parent-driven scope chain, the same
// defaults, the same validation, the same draft, the same payload. Only the
// layout differs, and layout is all that stays in the components.
//
// The hook is deliberately state-and-rules only. It never navigates, never
// renders, and never decides what happens after a task is created — that is the
// caller's question, and the two surfaces answer it differently (the page opens
// the task, the drawer keeps the list in place and clears itself).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useI18n,
  useProject,
  useTask,
  useTaskCustomField,
  useTaskTaskPolicy,
  useTaskTaskType,
  useTaskTemplate,
  useTeam,
} from '@react/hooks';

import { attachmentApi } from '@core/api';
import type {
  CreateTaskFromTemplateRequest,
  CustomFieldDefinition,
  ResolveTaskContextResponse,
  Task,
  TaskChecklist,
  TaskChecklistItem,
  TaskTemplate,
  TaskTypeDefinition,
} from '@core/types/task';
import type { Project } from '@core/types/project';
import type { Tag } from '@core/types/tag';
import type { Team } from '@core/types/team';

import { useScope } from '@/hooks/useScope';
import {
  assignTagsToEntity,
  buildCreatePayload,
  getEntityId,
  getPriorityColor,
  getPriorityIconPath,
  getStatusMapping,
  taskPriorityMap,
  validateTaskForm,
} from '@/features/task/logic';
import { errMsg } from '@/features/task/panelHelpers';
import type { UserPickerScope } from '@/components/UserPicker';

import {
  buildInitialCustomFields,
  customFieldId,
  defaultsForType,
  groupCustomFields,
  isCustomFieldEmpty,
  pickNumberingScheme,
  previewTaskKey,
  resolveCapabilities,
  selectCustomFieldDefinitions,
  synthesizeBaseTypeDefinitions,
  taskTypeEnumForDefinition,
  taskTypeIdForRequest,
  typeDefId,
  typeDefLabelKey,
  validateCustomFields,
  type CustomFieldDraftValue,
  type CustomFieldGroup,
  type FieldError,
} from './logic';
import {
  PARENT_KINDS,
  buildScopeChain,
  decorateTemplates,
  parentEntityFor,
  templateTaskTypeId,
  type ParentKind,
  type ParentType,
  type TemplateEntry,
} from './scope';
import {
  clearDraft,
  draftHasContent,
  draftKey,
  loadDraft,
  saveDraft,
  type ChecklistDraft,
  type WatcherDraft,
} from './draft';
import type { PendingFile } from './AttachmentDropzone';

export const EMPTY_TASK_FORM = {
  title: '',
  description: '',
  priority: 'TASK_PRIORITY_MEDIUM',
  assignee: '',
  assigneeName: '',
  due_date: '',
  start_date: '',
  estimated_hours: '',
};

export type TaskFormState = typeof EMPTY_TASK_FORM;

/** Section keys for the add-actions row. Custom-field groups add `cf:<group>`. */
export const SECTION_CHECKLISTS = 'checklists';
export const SECTION_WATCHERS = 'watchers';
export const SECTION_ATTACHMENTS = 'attachments';
export const customFieldSectionKey = (group: CustomFieldGroup) =>
  `cf:${group.key || '__ungrouped'}`;

/** Where the form starts: a deep link on the page, the scope in the drawer. */
export interface InitialParent {
  kind: ParentKind;
  id: string;
  /** Entity type name, needed only for the free-form `entity` kind. */
  typeName?: string;
}

export interface UseTaskCreateFormOptions {
  /** The parent the form opens on. Defaults to "no parent". */
  initialParent?: InitialParent;
  /** A template id from a deep link, applied once the context resolves. */
  templateId?: string | null;
  /** DOM id of the title field — the focus target when the title is invalid. */
  titleFieldId?: string;
  /**
   * What to do once the task exists. The hook has already cleared the draft;
   * the surface decides whether to open the task, or stay where it is.
   */
  onCreated?: (created: Task | null, createdId: string) => void;
}

/**
 * The parent as the picker and the scope chain understand it. Exposed so a
 * surface can re-point the form at a different container (the drawer follows
 * the workspace scope) without knowing how the chain is rebuilt.
 */
export interface ParentSelection {
  kind: ParentKind;
  id: string;
  typeName: ParentType;
}

export function useTaskCreateForm(options: UseTaskCreateFormOptions = {}) {
  const {
    initialParent,
    templateId: urlTemplateId,
    titleFieldId = 'task-create-title',
    onCreated,
  } = options;

  const { t } = useI18n();
  const scope = useScope();

  const store = useTask();
  const templateStore = useTaskTemplate();
  const customFieldStore = useTaskCustomField();
  const taskTypeStore = useTaskTaskType();
  const policyStore = useTaskTaskPolicy();
  const projectStore = useProject();
  const teamStore = useTeam();

  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  /** Fields the person has edited — resolved defaults never overwrite these. */
  const touched = useRef(new Set<string>());
  const restoredKey = useRef<string | null>(null);
  /** Mirrors `isDirty` so effects can read it without depending on it. */
  const dirtyRef = useRef(false);

  // ─── Parent scope ───────────────────────────────────────────────────────
  // The parent decides which types, templates, categories and numbering the
  // rest of this form offers, so changing it re-resolves everything. Its
  // default is "no parent": a deep link or a scoped workspace may say
  // otherwise, nothing else does.
  const [parentId, setParentId] = useState(initialParent?.id ?? '');
  // `kind` is what the picker offers; `otherTypeName` carries any entity type
  // outside the picked list (deep links, custom scopes) so those round-trip
  // instead of being silently rewritten to "project".
  const [parentKind, setParentKind] = useState<ParentKind>(initialParent?.kind ?? 'none');
  const [otherTypeName, setOtherTypeName] = useState(
    initialParent?.kind === 'entity' ? (initialParent.typeName ?? '') : '',
  );
  /**
   * The entity type name sent to the backend. "No parent" has none — it is the
   * absence of a parent, not a parent of an unnamed type.
   */
  const parentType: ParentType =
    parentKind === 'none' ? '' : parentKind === 'entity' ? otherTypeName.trim() : parentKind;

  // ─── Form ───────────────────────────────────────────────────────────────
  const [form, setForm] = useState<TaskFormState>(EMPTY_TASK_FORM);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [cfValues, setCfValues] = useState<Record<string, CustomFieldDraftValue>>({});
  const [cfLabels, setCfLabels] = useState<Record<string, string>>({});
  const [checklists, setChecklists] = useState<ChecklistDraft[]>([]);
  const [watchers, setWatchers] = useState<WatcherDraft[]>([]);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  /** Point the form at a container in one move — kind, id and type together. */
  const setParent = useCallback((next: InitialParent) => {
    setParentKind(next.kind);
    setParentId(next.id);
    setOtherTypeName(next.kind === 'entity' ? (next.typeName ?? '') : '');
    setSelectedTemplateId(null);
  }, []);

  // ─── Presentation ───────────────────────────────────────────────────────
  // Sections are open when they hold something; this map records explicit
  // choices on top of that, so collapsing never destroys anything.
  const [sectionOverrides, setSectionOverrides] = useState<Record<string, boolean>>({});
  const [descriptionPreview, setDescriptionPreview] = useState(false);
  const [errors, setErrors] = useState<Record<string, FieldError>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadWarning, setUploadWarning] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  // ─── ResolveTaskContext ─────────────────────────────────────────────────
  const [context, setContext] = useState<ResolveTaskContextResponse | null>(null);
  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState<string | null>(null);
  const [contextNonce, setContextNonce] = useState(0);
  const retryContext = useCallback(() => setContextNonce((n) => n + 1), []);

  // The records behind the parent picker. Their own `parent` is the next link
  // in the scope chain, so a project inside a team inherits the team's
  // templates and policy.
  const projects = (projectStore.items ?? []) as Project[];
  const teams = (teamStore.items ?? []) as Team[];

  const parentRecord = useMemo<Project | Team | null>(() => {
    if (!parentId) return null;
    if (parentKind === 'project') return projects.find((p) => getEntityId(p) === parentId) ?? null;
    if (parentKind === 'team') return teams.find((team) => getEntityId(team) === parentId) ?? null;
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, parentKind, projectStore.items, teamStore.items]);

  /** True when a real container was picked — "none" and "tenant" are not one. */
  const hasContainerParent =
    parentKind !== 'none' && parentKind !== 'tenant' && !!parentId && !!parentType;

  const { scopeEntity, chain } = useMemo(
    () =>
      buildScopeChain(
        scope,
        hasContainerParent
          ? { id: parentId, typeName: parentType, parent: parentRecord?.parent }
          : null,
      ),
    [scope, hasContainerParent, parentId, parentType, parentRecord],
  );
  const chainIds = useMemo(() => chain.map((entity) => entity.id).filter(Boolean), [chain]);

  const loadContext = useCallback(async () => {
    setContextLoading(true);
    setContextError(null);
    try {
      const response = await policyStore.ResolveTaskContext({
        // The scope the *answer* is resolved against. With no parent this is
        // the tenant — which is not the same as filing the task under it.
        scope_entity: scopeEntity,
        // Most-specific → least-specific. The server walks this to merge
        // policy and to collect templates; a single link would silently drop
        // everything inherited from the parent's own parent and the tenant.
        scope_chain: chain,
        task_type_id: selectedTypeId || undefined,
      });
      setContext(response);
    } catch (e) {
      setContext(null);
      setContextError(errMsg(e, t('task.createForm.contextError')));
    } finally {
      setContextLoading(false);
    }
    // `selectedTypeId` is intentionally not a dependency: re-resolving on every
    // type click would churn the list the person is clicking through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEntity, chain, policyStore, t]);

  useEffect(() => {
    void loadContext();
  }, [loadContext, contextNonce]);

  // Supporting catalogues. Each is a fallback for something the context call
  // may not carry on an unconfigured install.
  const loadProjects = useCallback(() => {
    void projectStore.ListProjects({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [projectStore]);

  const loadTeams = useCallback(() => {
    void teamStore.ListTeams({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [teamStore]);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadProjects, scope.productId]);

  useEffect(() => {
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTeams, scope.productId]);

  useEffect(() => {
    void customFieldStore.ListCustomFieldDefinitions({
      pagination: { limit: 200, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [customFieldStore]);

  useEffect(() => {
    void taskTypeStore.ListTaskTypes({
      pagination: { limit: 100, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
  }, [taskTypeStore]);

  // ─── Derived: types, policy, capabilities ───────────────────────────────
  const availableTypes: TaskTypeDefinition[] = useMemo(() => {
    const fromContext = context?.allowed_types ?? [];
    if (fromContext.length > 0) return fromContext.filter((d) => d.is_active !== false);
    const fromStore = (taskTypeStore.items ?? []) as TaskTypeDefinition[];
    if (fromStore.length > 0) return fromStore.filter((d) => d.is_active !== false);
    return synthesizeBaseTypeDefinitions();
  }, [context, taskTypeStore.items]);

  const policy = context?.effective_policy ?? null;

  const selectedType = useMemo(
    () => availableTypes.find((d) => typeDefId(d) === selectedTypeId) ?? availableTypes[0] ?? null,
    [availableTypes, selectedTypeId],
  );

  const capabilities = useMemo(
    () => resolveCapabilities(selectedType, policy),
    [selectedType, policy],
  );

  const categories = context?.available_categories ?? [];

  // First selection: the policy's default type, else the first allowed type.
  useEffect(() => {
    if (selectedTypeId || availableTypes.length === 0) return;
    const preferred =
      availableTypes.find((d) => typeDefId(d) === policy?.default_task_type_id) ?? availableTypes[0];
    setSelectedTypeId(typeDefId(preferred));
  }, [availableTypes, policy, selectedTypeId]);

  // ─── Draft ──────────────────────────────────────────────────────────────
  // One draft per (product, parent) pair — which is what makes the drawer and
  // the full page hand work to each other without retyping.
  const storageKey = useMemo(
    () => draftKey(scope.productId, parentId),
    [scope.productId, parentId],
  );

  useEffect(() => {
    if (restoredKey.current === storageKey) return;
    restoredKey.current = storageKey;
    // Switching the parent mid-compose changes the draft key. Whatever is
    // already on screen is newer than any stored draft, so it wins.
    if (dirtyRef.current) return;
    const draft = loadDraft(storageKey);
    if (!draftHasContent(draft) || !draft) {
      setDraftRestored(false);
      return;
    }
    setForm({
      title: draft.title,
      description: draft.description,
      priority: draft.priority || EMPTY_TASK_FORM.priority,
      assignee: draft.assignee,
      assigneeName: draft.assigneeName,
      due_date: draft.dueDate,
      start_date: draft.startDate,
      estimated_hours: draft.estimatedHours,
    });
    if (draft.typeId) setSelectedTypeId(draft.typeId);
    if (draft.parentKind) {
      const known = (PARENT_KINDS as readonly string[]).includes(draft.parentKind);
      setParentKind(known ? (draft.parentKind as ParentKind) : 'entity');
      // A free-form parent type lives in `parentType`; only the named kinds
      // carry no type name of their own.
      setOtherTypeName(
        !known
          ? draft.parentType || draft.parentKind
          : draft.parentKind === 'entity'
            ? draft.parentType || ''
            : '',
      );
    }
    setCategoryId(draft.categoryId);
    setSelectedTemplateId(draft.templateId);
    setTags(draft.tags || []);
    setCfValues(draft.customFieldValues || {});
    setCfLabels(draft.customFieldLabels || {});
    setChecklists(draft.checklists || []);
    setWatchers(draft.watchers || []);
    // A restored draft is the person's own work — never re-apply defaults over
    // it. Sections holding restored content open themselves, because openness
    // follows content.
    touched.current = new Set(['priority', 'assignee', 'estimated_hours', 'tags', 'type']);
    setDraftRestored(true);
  }, [storageKey]);

  const isDirty =
    form.title.trim() !== '' ||
    form.description.trim() !== '' ||
    tags.length > 0 ||
    watchers.length > 0 ||
    pendingFiles.length > 0 ||
    checklists.some((cl) => cl.items.some((it) => it.content.trim())) ||
    Object.values(cfValues).some((v) => (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim()));
  dirtyRef.current = isDirty;

  /** The draft as it stands right now — the shape sessionStorage holds. */
  const draftSnapshot = useCallback(
    () => ({
      title: form.title,
      description: form.description,
      typeId: selectedTypeId,
      categoryId,
      priority: form.priority,
      assignee: form.assignee,
      assigneeName: form.assigneeName,
      dueDate: form.due_date,
      startDate: form.start_date,
      estimatedHours: form.estimated_hours,
      parentId,
      parentKind,
      parentType,
      templateId: selectedTemplateId,
      tags,
      customFieldValues: cfValues,
      customFieldLabels: cfLabels,
      checklists,
      watchers,
    }),
    [
      form,
      selectedTypeId,
      categoryId,
      parentId,
      parentKind,
      parentType,
      selectedTemplateId,
      tags,
      cfValues,
      cfLabels,
      checklists,
      watchers,
    ],
  );

  useEffect(() => {
    if (!isDirty) return;
    const handle = window.setTimeout(() => saveDraft(storageKey, draftSnapshot()), 400);
    return () => window.clearTimeout(handle);
  }, [isDirty, storageKey, draftSnapshot]);

  /**
   * Write the draft now rather than in 400ms. Handing the work to the other
   * surface (drawer → full page) happens on a click, and a click is faster
   * than the debounce.
   */
  const flushDraft = useCallback(() => {
    if (!dirtyRef.current) return;
    saveDraft(storageKey, draftSnapshot());
  }, [storageKey, draftSnapshot]);

  const discardDraft = useCallback(() => {
    clearDraft(storageKey);
    setForm(EMPTY_TASK_FORM);
    setTags([]);
    setCfValues({});
    setCfLabels({});
    setChecklists([]);
    setWatchers([]);
    setPendingFiles([]);
    setSelectedTemplateId(null);
    setCategoryId('');
    setErrors({});
    setSectionOverrides({});
    setDraftRestored(false);
    setUploadWarning(false);
    touched.current = new Set();
  }, [storageKey]);

  // ─── Defaults: category → policy (container merged) → type ──────────────
  // One pass, one precedence order. The category is the most specific answer,
  // the effective policy already carries whatever the container contributed,
  // and the type definition is the base. Anything the person touched is theirs.
  const selectedCategory = categories.find((c) => getEntityId(c) === categoryId) ?? null;

  useEffect(() => {
    const typeDefaults = defaultsForType(selectedType);

    if (!touched.current.has('type')) {
      const defaultTypeId = selectedCategory?.default_task_type_id || policy?.default_task_type_id;
      if (defaultTypeId && availableTypes.some((d) => typeDefId(d) === defaultTypeId)) {
        setSelectedTypeId(defaultTypeId);
      }
    }

    const priority =
      (selectedCategory?.default_priority as string | undefined) ||
      (policy?.default_priority as string | undefined) ||
      typeDefaults.priority;
    const assigneeId = policy?.default_assignee?.entity?.id || typeDefaults.assigneeId;
    const assigneeName = policy?.default_assignee?.display_name || typeDefaults.assigneeName;

    setForm((prev) => ({
      ...prev,
      priority: touched.current.has('priority') ? prev.priority : priority || prev.priority,
      assignee: touched.current.has('assignee') ? prev.assignee : assigneeId || prev.assignee,
      assigneeName: touched.current.has('assignee')
        ? prev.assigneeName
        : assigneeName || prev.assigneeName,
      estimated_hours: touched.current.has('estimated_hours')
        ? prev.estimated_hours
        : typeDefaults.estimatedHours || prev.estimated_hours,
    }));

    if (!touched.current.has('tags') && typeDefaults.tags.length > 0) {
      setTags((prev) => {
        const existing = new Set(prev.map((tag) => tag.name));
        const added = typeDefaults.tags
          .filter((name) => !existing.has(name))
          .map((name) => ({ name }) as Tag);
        return added.length ? [...prev, ...added] : prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, policy, selectedCategory]);

  // ─── Templates ──────────────────────────────────────────────────────────
  // `ResolveTaskContext.available_templates` is the single source of truth: it
  // is already the union over the parent's scope chain, already ordered
  // most-specific-first, and already filtered to the selected task type.
  const templateEntries: TemplateEntry[] = useMemo(() => {
    const fromContext = (context?.available_templates ?? []).filter(
      (tpl) => tpl.is_active !== false,
    );
    return decorateTemplates(fromContext, parentId, chainIds);
  }, [context, parentId, chainIds]);

  const applyTemplate = useCallback(
    (template: TaskTemplate | null) => {
      if (!template) {
        setSelectedTemplateId(null);
        return;
      }
      setSelectedTemplateId(getEntityId(template) || null);
      // A template may declare the task type it belongs to; honour it over the
      // legacy `default_type` enum when present.
      const boundTypeId = templateTaskTypeId(template);
      if (boundTypeId) setSelectedTypeId(boundTypeId);
      setForm((prev) => ({
        ...prev,
        title: prev.title || template.default_title || '',
        description: prev.description || template.default_description || '',
        priority: template.default_priority || prev.priority,
      }));
      if (!boundTypeId && template.default_type) {
        const match = availableTypes.find(
          (d) => taskTypeEnumForDefinition(d) === template.default_type,
        );
        if (match) setSelectedTypeId(typeDefId(match));
      }
      const templateChecklists = template.default_checklists?.checklists ?? [];
      if (templateChecklists.length > 0) {
        // The checklist section opens itself: it now holds content.
        setChecklists(
          templateChecklists.map((cl: TaskChecklist) => ({
            title: cl.title || t('task.checklist'),
            items: ((cl.items as { items?: TaskChecklistItem[] } | undefined)?.items ?? []).map(
              (item) => ({
                id: crypto.randomUUID(),
                content: item.content || '',
                is_done: false,
              }),
            ),
          })),
        );
      }
    },
    [availableTypes, t],
  );

  // A template id in the URL is a deliberate entry point. Prefer the resolved
  // list; if the link points outside this parent's scope, fetch that one
  // template by id rather than listing templates the parent does not own.
  const appliedUrlTemplate = useRef(false);
  useEffect(() => {
    if (!urlTemplateId || appliedUrlTemplate.current || contextLoading) return;
    const known = templateEntries.find((entry) => entry.id === urlTemplateId);
    if (known) {
      appliedUrlTemplate.current = true;
      applyTemplate(known.template);
      return;
    }
    appliedUrlTemplate.current = true;
    templateStore
      .GetTemplate(urlTemplateId)
      .then((template) => applyTemplate(template))
      .catch(() => {
        /* A dead template link must not block creating the task. */
      });
  }, [urlTemplateId, templateEntries, contextLoading, applyTemplate, templateStore]);

  // ─── Numbering preview ──────────────────────────────────────────────────
  const numberingScheme = useMemo(
    () =>
      pickNumberingScheme(context?.available_numbering_schemes, {
        typeId: selectedTypeId,
        categoryId,
      }),
    [context, selectedTypeId, categoryId],
  );
  const keyPreview = previewTaskKey(numberingScheme);

  // ─── Custom fields ──────────────────────────────────────────────────────
  const visibleCustomFields = useMemo(() => {
    if (!capabilities.customFields) return [];
    const all = (customFieldStore.items ?? []) as CustomFieldDefinition[];
    return selectCustomFieldDefinitions(all, selectedType, policy);
  }, [capabilities.customFields, customFieldStore.items, selectedType, policy]);

  const customFieldGroups = useMemo(
    () => groupCustomFields(visibleCustomFields),
    [visibleCustomFields],
  );

  // ─── Sections ───────────────────────────────────────────────────────────
  /** Whether the type's `preset` says this person wants everything open. */
  const advancedPreset = selectedType?.preset === 'TASK_TYPE_PRESET_ADVANCED';

  const checklistItemCount = checklists.reduce(
    (total, cl) => total + cl.items.filter((item) => item.content.trim()).length,
    0,
  );

  const filledInGroup = useCallback(
    (group: CustomFieldGroup): number =>
      group.fields.filter((def) => !isCustomFieldEmpty(cfValues[customFieldId(def)])).length,
    [cfValues],
  );

  /** A section is open when it holds content, unless explicitly toggled. */
  const sectionOpen = useCallback(
    (key: string, holdsContent: boolean): boolean => sectionOverrides[key] ?? holdsContent,
    [sectionOverrides],
  );

  const toggleSection = useCallback(
    (key: string, holdsContent: boolean) =>
      setSectionOverrides((prev) => ({ ...prev, [key]: !(prev[key] ?? holdsContent) })),
    [],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────
  const setField = useCallback(
    (field: keyof TaskFormState, value: string) => {
      touched.current.add(field);
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => {
        if (!prev[field]) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [],
  );

  const setAssignee = useCallback((userId: string, displayName: string) => {
    touched.current.add('assignee');
    setForm((prev) => ({ ...prev, assignee: userId, assigneeName: displayName }));
    setErrors((prev) => {
      if (!prev.assignee) return prev;
      const next = { ...prev };
      delete next.assignee;
      return next;
    });
  }, []);

  const setTagsTouched = useCallback((next: Tag[]) => {
    touched.current.add('tags');
    setTags(next);
  }, []);

  const setCustomField = useCallback(
    (fieldId: string, value: CustomFieldDraftValue, label?: string) => {
      setCfValues((prev) => ({ ...prev, [fieldId]: value }));
      if (label !== undefined) setCfLabels((prev) => ({ ...prev, [fieldId]: label }));
      setErrors((prev) => {
        if (!prev[fieldId]) return prev;
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    },
    [],
  );

  const handleAddFiles = useCallback((incoming: FileList | File[]) => {
    const added: PendingFile[] = Array.from(incoming).map((file) => ({
      id: crypto.randomUUID(),
      file,
    }));
    setPendingFiles((prev) => [...prev, ...added]);
    for (const item of added) {
      if (!item.file.type.startsWith('image/')) continue;
      const reader = new FileReader();
      reader.onload = () => {
        setPendingFiles((prev) =>
          prev.map((f) => (f.id === item.id ? { ...f, previewUrl: String(reader.result) } : f)),
        );
      };
      reader.readAsDataURL(item.file);
    }
  }, []);

  const uploadFiles = async (): Promise<{ ids: string[]; failed: number }> => {
    const ids: string[] = [];
    let failed = 0;
    for (const pending of pendingFiles) {
      try {
        const urlResponse = (await attachmentApi.GetUploadUrl({
          file_name: pending.file.name,
          mime_type: pending.file.type || 'application/octet-stream',
          size_bytes: pending.file.size,
        } as never)) as { upload_url?: { url?: string }; upload_id?: string };
        const uploadUrl = urlResponse?.upload_url?.url;
        const uploadId = urlResponse?.upload_id;
        if (!uploadUrl || !uploadId) {
          failed++;
          continue;
        }
        await fetch(uploadUrl, {
          method: 'PUT',
          body: pending.file,
          headers: { 'Content-Type': pending.file.type || 'application/octet-stream' },
        });
        const confirmed = (await attachmentApi.ConfirmUpload({ upload_id: uploadId } as never)) as {
          entity?: { id?: string };
          attachment?: { entity?: { id?: string } };
        };
        const attachmentId = confirmed?.entity?.id || confirmed?.attachment?.entity?.id;
        if (attachmentId) ids.push(attachmentId);
        else failed++;
      } catch {
        failed++;
      }
    }
    return { ids, failed };
  };

  const focusFirstError = (nextErrors: Record<string, FieldError>) => {
    window.requestAnimationFrame(() => {
      if (nextErrors.title) {
        document.getElementById(titleFieldId)?.focus();
        return;
      }
      const anchor = formRef.current?.querySelector<HTMLElement>('[data-field-error="true"]');
      if (!anchor) return;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      anchor.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      // A pill *is* the control; a field wrapper *contains* one.
      const focusable = 'input, textarea, button, [tabindex]:not([tabindex="-1"])';
      const target = anchor.matches(focusable)
        ? anchor
        : anchor.querySelector<HTMLElement>(focusable);
      target?.focus();
    });
  };

  const submit = async () => {
    const nextErrors: Record<string, FieldError> = {};

    for (const [field, key] of Object.entries(validateTaskForm(form))) {
      nextErrors[field] = { key, params: { field: t('task.field.title'), min: 2, max: 200 } };
    }
    if (capabilities.requiresAssignee && !form.assignee) {
      nextErrors.assignee = { key: 'task.assigneeRequired' };
    }
    if (form.start_date && form.due_date && form.start_date > form.due_date) {
      nextErrors.due_date = { key: 'task.createForm.error.dateOrder' };
    }
    Object.assign(nextErrors, validateCustomFields(visibleCustomFields, cfValues));

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      // An invalid custom field may sit inside a collapsed section — open it,
      // otherwise the message would be announced about something unseen.
      const reopen: Record<string, boolean> = {};
      for (const group of customFieldGroups) {
        if (group.fields.some((def) => nextErrors[customFieldId(def)])) {
          reopen[customFieldSectionKey(group)] = true;
        }
      }
      if (Object.keys(reopen).length > 0) {
        setSectionOverrides((prev) => ({ ...prev, ...reopen }));
      }
      focusFirstError(nextErrors);
      return;
    }

    setErrors({});
    setUploadWarning(false);
    setIsSubmitting(true);

    // "No parent" sends no parent at all; "the whole tenant" sends the tenant.
    const parentEntity = parentEntityFor(scope, parentKind, parentId, parentType);

    try {
      // Template path — the server expands the template (resources, workflow
      // hooks); only these four fields are overridable per the RPC contract.
      if (selectedTemplateId) {
        const overrides: CreateTaskFromTemplateRequest = {
          title_override: form.title.trim() || undefined,
          parent: parentEntity ?? undefined,
          assignee: form.assignee
            ? { entity: { id: form.assignee }, display_name: form.assigneeName || '' }
            : undefined,
          due_date: form.due_date ? new Date(form.due_date).toISOString() : undefined,
        } as CreateTaskFromTemplateRequest;
        const created = await store.CreateTaskFromTemplate(selectedTemplateId, overrides);
        const createdId = getEntityId(created);
        if (createdId && tags.length > 0) await assignTagsToEntity(tags, createdId, 'task');
        clearDraft(storageKey);
        onCreated?.((created ?? null) as Task | null, createdId);
        return;
      }

      const payload = buildCreatePayload({
        title: form.title,
        description: form.description,
        type: taskTypeEnumForDefinition(selectedType),
        priority: form.priority,
        assignee: form.assignee,
        assignee_name: form.assigneeName,
        due_date: form.due_date,
      });

      if (parentEntity) payload.parent = parentEntity;

      // The base enum says "a task"; the registry id says *which* task type,
      // and with it the statuses, transitions, templates and numbering.
      const taskTypeId = taskTypeIdForRequest(selectedType);
      if (taskTypeId) payload.task_type_id = taskTypeId;
      if (categoryId) payload.category_id = categoryId;

      if (form.start_date) payload.start_date = new Date(form.start_date).toISOString();
      const hours = parseFloat(form.estimated_hours);
      if (form.estimated_hours && !Number.isNaN(hours) && hours > 0) {
        payload.estimated_duration = `${Math.round(hours * 3600)}s`;
      }
      if (tags.length > 0) {
        payload.tags = tags.map((tag) => tag.name || tag.entity?.id).filter(Boolean);
      }

      if (pendingFiles.length > 0) {
        const { ids, failed } = await uploadFiles();
        if (ids.length > 0) payload.attachments = { entities: ids.map((id) => ({ id })) };
        if (failed > 0) setUploadWarning(true);
      }

      const initialCustomFields = buildInitialCustomFields(visibleCustomFields, cfValues, cfLabels);
      if (initialCustomFields.length > 0) payload.initial_custom_fields = initialCustomFields;

      if (capabilities.checklists) {
        const initialChecklists = checklists
          .map((cl) => ({
            title: cl.title,
            items: {
              items: cl.items
                .filter((item) => item.content.trim())
                .map((item, index) => ({
                  content: item.content,
                  is_done: item.is_done,
                  order: index,
                })),
            },
          }))
          .filter((cl) => cl.items.items.length > 0 || cl.title.trim());
        if (initialChecklists.length > 0) payload.initial_checklists = initialChecklists;
      }

      if (capabilities.watchers && watchers.length > 0) {
        payload.initial_watchers = watchers.map((w) => ({
          entity: { id: w.id },
          display_name: w.name,
        }));
      }

      const created = await store.CreateTask(payload);
      const createdId = getEntityId(created);
      if (createdId && tags.length > 0) await assignTagsToEntity(tags, createdId, 'task');
      clearDraft(storageKey);
      onCreated?.((created ?? null) as Task | null, createdId);
    } catch {
      // The store surfaces the failure; the draft stays so nothing is lost.
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = form.title.trim().length > 0 && !isSubmitting;

  // ⌘/Ctrl+Enter submits from anywhere in the form. Escape is deliberately not
  // bound here — the surface owns what Escape means for it.
  const submitRef = useRef(submit);
  submitRef.current = submit;
  const submitNow = useCallback(() => submitRef.current(), []);

  // ─── Rendering helpers ──────────────────────────────────────────────────
  // These read `t`, so they live here rather than being written twice.
  const errorText = useCallback(
    (field: string): string | undefined => {
      const error = errors[field];
      return error ? t(error.key, error.params) : undefined;
    },
    [errors, t],
  );

  const typeLabel = useCallback(
    (def: TaskTypeDefinition | null): string => {
      if (!def) return '';
      const key = typeDefLabelKey(def);
      return key ? t(key) : def.display_name || def.slug;
    },
    [t],
  );

  const projectLabel = (project: Project): string =>
    project.key ? `${project.key} — ${project.name}` : project.name || getEntityId(project);

  /** Only the parent kinds that can actually be picked. */
  const parentKinds = PARENT_KINDS.filter((kind) => {
    if (kind === 'project') return projects.length > 0 || parentKind === 'project';
    if (kind === 'team') return teams.length > 0 || parentKind === 'team';
    return true;
  });

  /** Options for the parent value picker, per selected parent type. */
  const parentOptions =
    parentKind === 'project'
      ? projects.map((project) => ({ value: getEntityId(project), label: projectLabel(project) }))
      : parentKind === 'team'
        ? teams.map((team) => ({ value: getEntityId(team), label: team.name || getEntityId(team) }))
        : [];

  const parentIsLoading =
    (parentKind === 'project' && projectStore.isLoading && projects.length === 0) ||
    (parentKind === 'team' && teamStore.isLoading && teams.length === 0);

  const parentLoadError =
    (parentKind === 'project' && !projectStore.isLoading && !!projectStore.error) ||
    (parentKind === 'team' && !teamStore.isLoading && !!teamStore.error);

  const retryParents = useCallback(() => {
    if (parentKind === 'project') loadProjects();
    else loadTeams();
  }, [parentKind, loadProjects, loadTeams]);

  /**
   * What the parent reads as. "No parent" is an answer, not an absence, so it
   * reads as a chosen value; a kind picked without an instance yet is the only
   * genuinely unset state here.
   */
  const parentValue =
    parentKind === 'none'
      ? t('task.createForm.parent.none')
      : parentKind === 'tenant'
        ? t('task.createForm.parent.tenant')
        : parentId
          ? (parentOptions.find((option) => option.value === parentId)?.label ??
            `${parentType || t('task.createForm.parent.entity')}: ${parentId.slice(0, 8)}`)
          : '';

  /** How the current parent reads in prose (template origins, popover hint). */
  const parentDisplayName = parentValue || t(`task.createForm.parent.${parentKind}`);

  const formatDate = useCallback((value: string): string => {
    if (!value) return '';
    const parsed = new Date(`${value}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }, []);

  const categoryValue = selectedCategory
    ? selectedCategory.code
      ? `${selectedCategory.code} — ${selectedCategory.name}`
      : selectedCategory.name
    : '';

  const tagsValue =
    tags.length === 0
      ? ''
      : tags.length <= 2
        ? tags.map((tag) => tag.name).join(', ')
        : t('task.createForm.tagCount', { count: tags.length });

  const priorityValue = t(getStatusMapping(taskPriorityMap, form.priority).labelKey);
  const priorityIcon = getPriorityIconPath(form.priority);
  const priorityIconClass = getPriorityColor(form.priority);

  /** The scope the people-pickers list from: this parent, when it is one. */
  const pickerScope: UserPickerScope | undefined = hasContainerParent
    ? { typeName: parentType, id: parentId, label: parentDisplayName }
    : undefined;

  // ─── Required work still outstanding ────────────────────────────────────
  // Said before Create is pressed, not after it is refused.
  const missingRequired: string[] = [];
  if (!form.title.trim()) missingRequired.push(t('task.field.title'));
  if (capabilities.requiresAssignee && !form.assignee) {
    missingRequired.push(t('task.field.assignee'));
  }
  for (const def of visibleCustomFields) {
    if (
      def.is_required &&
      def.field_type !== 'CUSTOM_FIELD_TYPE_CHECKBOX' &&
      isCustomFieldEmpty(cfValues[customFieldId(def)])
    ) {
      missingRequired.push(def.display_name);
    }
  }

  /**
   * What creating from a template cannot carry. `CreateTaskFromTemplate` takes
   * four overrides — title, parent, assignee, due date — so anything else set
   * here would be quietly lost. It is named instead.
   */
  const templateDropped: string[] = [];
  if (selectedTemplateId) {
    if (form.description.trim()) templateDropped.push(t('task.field.description'));
    if (touched.current.has('priority')) templateDropped.push(t('task.field.priority'));
    if (form.start_date) templateDropped.push(t('task.startDate'));
    if (form.estimated_hours) templateDropped.push(t('task.estimatedHours'));
    if (categoryId) templateDropped.push(t('task.categories.title'));
    if (checklistItemCount > 0) templateDropped.push(t('task.checklists'));
    if (watchers.length > 0) templateDropped.push(t('task.watchers'));
    if (pendingFiles.length > 0) templateDropped.push(t('task.attachments'));
    if (Object.values(cfValues).some((v) => !isCustomFieldEmpty(v))) {
      templateDropped.push(t('task.customFields'));
    }
  }

  return {
    // stores + scope
    store,
    projectStore,
    teamStore,
    scope,
    customFieldsLoading: customFieldStore.isLoading,

    // refs
    descriptionRef,
    formRef,
    touched,
    titleFieldId,

    // parent
    parentKind,
    setParentKind,
    parentId,
    setParentId,
    otherTypeName,
    setOtherTypeName,
    parentType,
    setParent,
    parentKinds,
    parentOptions,
    parentIsLoading,
    parentLoadError,
    retryParents,
    parentValue,
    parentDisplayName,
    hasContainerParent,
    pickerScope,

    // form
    form,
    setForm,
    setField,
    setAssignee,
    errors,
    setErrors,
    errorText,
    isSubmitting,
    submit,
    submitNow,
    canSubmit,
    missingRequired,

    // type / category / policy
    availableTypes,
    selectedType,
    selectedTypeId,
    setSelectedTypeId,
    typeLabel,
    categories,
    categoryId,
    setCategoryId,
    categoryValue,
    policy,
    capabilities,

    // priority / tags / dates
    priorityValue,
    priorityIcon,
    priorityIconClass,
    tags,
    setTags: setTagsTouched,
    tagsValue,
    formatDate,

    // context
    context,
    contextLoading,
    contextError,
    retryContext,

    // templates
    templateEntries,
    selectedTemplateId,
    applyTemplate,
    templateDropped,

    // numbering
    numberingScheme,
    keyPreview,

    // custom fields
    visibleCustomFields,
    customFieldGroups,
    cfValues,
    cfLabels,
    setCustomField,
    filledInGroup,

    // secondary blocks
    checklists,
    setChecklists,
    checklistItemCount,
    watchers,
    setWatchers,
    pendingFiles,
    setPendingFiles,
    handleAddFiles,
    uploadWarning,

    // sections
    advancedPreset,
    sectionOpen,
    toggleSection,

    // description preview
    descriptionPreview,
    setDescriptionPreview,

    // draft
    isDirty,
    draftRestored,
    setDraftRestored,
    discardDraft,
    flushDraft,
    storageKey,
  };
}

export type TaskCreateForm = ReturnType<typeof useTaskCreateForm>;
